import styled from '@emotion/styled';
import { AppBar as _AppBar, Button, Toolbar } from '@material-ui/core';
import { ClassAttributes, createElement, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';

import { UserOptimizer } from '../../../../graphql/generated/graphql';
import { SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import _KaizenAdLogo from '../../primitive/KaizenAdLogo';
import _UserMenu from './UserMenu';

export const HEADBAR_HEIGHT = 56;

interface Props {
  signedInUser: UserOptimizer;
  language: string;
  onClickProjectsButton: MouseEventHandler<HTMLElement>;
  onClickOffersButton: MouseEventHandler<HTMLElement>;
  onClickLogo: MouseEventHandler<HTMLElement>;
  onClickSettings: MouseEventHandler<HTMLElement>;
  hugotDirWebUrl: string;
  profileWebUrl: string;
  onClickSignOut: MouseEventHandler<HTMLElement>;
  onClickAccountSettings: MouseEventHandler<HTMLElement>;
  className?: string;
}

const HeadBar = ({
  signedInUser,
  language,
  onClickProjectsButton,
  onClickOffersButton,
  onClickLogo,
  onClickSettings,
  hugotDirWebUrl,
  profileWebUrl,
  onClickSignOut,
  onClickAccountSettings,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  return (
    <AppBar color="default" elevation={2} className={className}>
      <StyledToolbar>
        <LogoContainer onClick={onClickLogo}>
          <KaizenAdLogo />
        </LogoContainer>

        <LinkButtonsContainer>
          <ProjectsButton onClick={onClickProjectsButton}>
            <FormattedMessage id="headbar.projects" />
          </ProjectsButton>

          <OffersButton onClick={onClickOffersButton}>
            <FormattedMessage id="headbar.offers" />
          </OffersButton>
          {language === 'ja-JP' ? (
            <SearchLink href={hugotDirWebUrl + '/search'} target="_blank" rel="noopener noreferrer">
              <SearchButton>
                <FormattedMessage id="headbar.creative_search" />
              </SearchButton>
            </SearchLink>
          ) : null}
        </LinkButtonsContainer>

        <UserMenu
          signedInUser={signedInUser}
          onClickSettings={onClickSettings}
          profileWebUrl={profileWebUrl}
          onClickSignOut={onClickSignOut}
          onClickAccountSettings={onClickAccountSettings}
        />
      </StyledToolbar>
    </AppBar>
  );
};

const AppBar = styled(_AppBar)({
  backgroundColor: '#fff',
});

const StyledToolbar = styled(Toolbar)(
  gridLayoutContainer({
    columns: ['180px', 'auto', '1fr', 'auto', 'auto'],
    rows: ['1fr'],
    areas: [['logo', 'link_buttons', '.', 'organization_select', 'user_menu']],
    alignItems: 'center',
  }),
  {
    gridColumnGap: SPACING_UNIT * 3,
    height: HEADBAR_HEIGHT,
    minHeight: HEADBAR_HEIGHT,
  },
);

export const LogoContainer = styled('div')(gridLayoutItem('logo'), {
  justifySelf: 'flex-start',
  cursor: 'pointer',
});

const KaizenAdLogo = styled(_KaizenAdLogo)({
  paddingBottom: 2,
  width: 100,
});

const LinkButtonsContainer = styled('div')(
  gridLayoutItem('link_buttons'),
  flexibleLayoutContainer({ direction: 'row' }),
);

export const ProjectsButton = styled(Button)({
  marginRight: SPACING_UNIT,
});

export const OffersButton = styled(Button)({
  marginRight: SPACING_UNIT,
});

export const SearchLink = styled('a')({
  textDecoration: 'none',
});

export const SearchButton = styled(Button)();

export const UserMenu = styled(_UserMenu)(gridLayoutItem('user_menu'));

export default HeadBar;
